/**
 * Tatvex Studio - Main Application Component
 *
 * React Router setup with authenticated routes:
 * - /login - Login page (public)
 * - /studio - Dashboard (protected)
 * - /studio/:vertical - Vertical editor (protected)
 * - /studio/:vertical/:configType - Config file editor (protected)
 * - /studio/account/settings - Account settings (protected)
 * - /studio/admin/* - Admin pages (protected, capability-gated)
 */

import type { ReactElement } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthenticatedLayout } from "./layouts/AuthenticatedLayout";
import { ApprovalQueuePage } from "./pages/ApprovalQueuePage";
import { BrandingEditorPage } from "./pages/BrandingEditorPage";
import { ConfigEditorPage } from "./pages/ConfigEditorPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DiffPageRoute } from "./pages/DiffPageRoute";
import { LoginPage } from "./pages/LoginPage";
import { VerticalEditorPage } from "./pages/VerticalEditorPage";
import { AccountSettingsPage } from "./pages/account/AccountSettingsPage";
import { ClickAnalyticsDashboardPage } from "./pages/admin/analytics/ClickAnalyticsDashboardPage";
import { AuditLogsPage } from "./pages/admin/audit/AuditLogsPage";
import { HealthDashboardPage } from "./pages/admin/health/HealthDashboardPage";
import { RoleManagementPage } from "./pages/admin/roles/RoleManagementPage";
import { DomainMappingsPage } from "./pages/admin/tenants/DomainMappingsPage";
import { TenantCreatePage } from "./pages/admin/tenants/TenantCreatePage";
import { TenantDetailPage } from "./pages/admin/tenants/TenantDetailPage";
import { TenantListPage } from "./pages/admin/tenants/TenantListPage";
import { TenantOnboardingWizard } from "./pages/admin/tenants/TenantOnboardingWizard";
import { TenantUsagePage } from "./pages/admin/tenants/TenantUsagePage";
import { UserDetailPage } from "./pages/admin/users/UserDetailPage";
import { UserInvitePage } from "./pages/admin/users/UserInvitePage";
import { UserListPage } from "./pages/admin/users/UserListPage";
import { ProviderDetailPage } from "./pages/providers/ProviderDetailPage";
import { ProviderListPage } from "./pages/providers/ProviderListPage";

export default function App(): ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes - wrapped in AuthenticatedLayout */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="/studio" element={<DashboardPage />} />
          <Route path="/studio/:verticalKey" element={<VerticalEditorPage />} />
          <Route path="/studio/:verticalKey/providers" element={<ProviderListPage />} />
          <Route
            path="/studio/:verticalKey/providers/:providerId"
            element={<ProviderDetailPage />}
          />
          <Route path="/studio/:verticalKey/branding" element={<BrandingEditorPage />} />
          <Route path="/studio/:verticalKey/:configType" element={<ConfigEditorPage />} />
          <Route path="/studio/:verticalKey/:configType/diff" element={<DiffPageRoute />} />
          <Route path="/studio/approvals" element={<ApprovalQueuePage />} />

          {/* Account routes */}
          <Route path="/studio/account/settings" element={<AccountSettingsPage />} />

          {/* Admin routes - capability-gated in components */}
          <Route path="/studio/admin/tenants" element={<TenantListPage />} />
          <Route path="/studio/admin/tenants/create" element={<TenantCreatePage />} />
          <Route path="/studio/admin/tenants/onboard" element={<TenantOnboardingWizard />} />
          <Route path="/studio/admin/tenants/:tenantId" element={<TenantDetailPage />} />
          <Route path="/studio/admin/tenants/:tenantId/domains" element={<DomainMappingsPage />} />
          <Route path="/studio/admin/tenants/:tenantId/usage" element={<TenantUsagePage />} />
          <Route path="/studio/admin/tenants/:tenantId/roles" element={<RoleManagementPage />} />
          <Route path="/studio/admin/users" element={<UserListPage />} />
          <Route path="/studio/admin/users/invite" element={<UserInvitePage />} />
          <Route path="/studio/admin/users/:userId" element={<UserDetailPage />} />
          <Route path="/studio/admin/audit" element={<AuditLogsPage />} />
          <Route path="/studio/admin/analytics" element={<ClickAnalyticsDashboardPage />} />
          <Route path="/studio/admin/health" element={<HealthDashboardPage />} />
        </Route>

        {/* Redirect root to studio */}
        <Route path="/" element={<Navigate to="/studio" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/studio" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
